import { BreakPoint, windowSize } from '@adornis/base/client/frontend-helpers/window-size.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { trn } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import {
  mergeRegistrationColorDefinitions,
  RegistrationColorDefinitions,
} from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import type { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import type { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { formatPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import { getDevice } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import type { Language } from '@adornis/translation-core/languages.js';
import { html, nothing, type TemplateResult } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

export const windowSizeObservable = DesignSystem.currentTheme.pipe(
  switchMap(theme =>
    windowSize.pipe(
      debounceTime(100),
      startWith({
        width: window.innerWidth,
        height: window.innerHeight,
        device: getDevice(window.innerWidth, theme.sizes.breakPoints),
      }),
      map(size => {
        const device = getDevice(size.width, theme.sizes.breakPoints);
        return {
          height: size.height,
          width: size.width,
          device,
          tablet: device <= BreakPoint.tablet,
          mobile: device <= BreakPoint.bigPhone,
          mobileSmall: device <= BreakPoint.smallPhone,
          landscape: size.height < size.width,
        };
      }),
      shareReplay(1),
    ),
  ),
);

export const componentSizeObservable = (element: Element) =>
  DesignSystem.currentTheme.pipe(
    switchMap(theme =>
      new Observable(subscriber =>
        new ResizeObserver(entries => subscriber.next(entries[0].contentRect)).observe(element),
      ).pipe(
        debounceTime(25),
        startWith({
          bottom: 0,
          height: 0,
          left: 0,
          right: 0,
          top: 0,
          width: 0,
          x: 0,
          y: 0,
        }),
        map(domRect => ({
          bottom: domRect.bottom,
          height: domRect.height,
          left: domRect.left,
          right: domRect.right,
          top: domRect.top,
          width: domRect.width,
          x: domRect.x,
          y: domRect.y,
          componentDevice: getDevice(domRect.width, theme.sizes.breakPoints),
        })),
        shareReplay(1),
      ),
    ),
  );

// TODO: rename because name does not work with workshop set
export type ProductCardData = {
  start: Maybe<DateTime>;
  end: Maybe<DateTime>;
  colorDef: NarrowedEntity<RegistrationColorDefinitions, ColorDefinitionsSelectionSet['registrationColorDefinitions']>;
  price?: number | [number, number];
  applyedBundle?: {
    price?: number | [number, number];
  };
};

type ColorDefinitionsSelectionSet = {
  accentColor: 1;
  registrationColorDefinitions: {
    stepColor: 1;
    moreProductsFontColor: 1;
    moreProductsColor: 1;
    productColor: 1;
    productFontColor: 1;
    newPriceColor: 1;
    oldPriceColor: 1;
    layoutLightContrastColor: 1;
    warnColor: 1;
  };
};

export function getRegistrationColorDefinitionsForInstitution(
  institution: NarrowedEntity<
    Institution,
    {
      settings: {
        colorDefinitions: ColorDefinitionsSelectionSet;
      };
      inheritedColorDefinitions: { value: ColorDefinitionsSelectionSet };
    }
  >,
) {
  const colors = DesignSystem.currentTheme.value.colors;
  return mergeRegistrationColorDefinitions(
    institution.settings.colorDefinitions?.registrationColorDefinitions,
    institution.settings.inheritedColorDefinitions?.value?.registrationColorDefinitions,
    new RegistrationColorDefinitions({
      stepColor: colors.accent,
      stepFontColor: '#fff',
      productColor: colors.tone.black,
      productFontColor: '#fff',
      moreProductsColor: 'white',
      moreProductsFontColor: colors.tone.black,
      chipInfosColor: colors.accent,
      chipInfosFontColor: '#fff',
      oldPriceColor: '#000',
      newPriceColor: colors.tone.error,
      layoutLightContrastColor: 'lightgray',
      warnColor: '#debb52',
    }),
  );
}

export function renderPrice(
  newPrice: number | [number, number] | null,
  oldPrice: Maybe<number | [number, number]> = null,
  newPriceColor: string,
  oldPriceColor: string,
  isBundle: boolean = false,
) {
  if (oldPrice === null)
    return html`
      <tr-text bold ${css({ fontSize: '24px', color: newPriceColor })}> ${formatPrice(newPrice)} </tr-text>
    `;

  const oldPriceStr = formatPrice(oldPrice, '0,00 €');
  if (newPrice === null || (!(newPrice instanceof Array) && !isNaN(newPrice) && newPrice < 0))
    return renderBundledPrice('0,00 €', newPriceColor, oldPriceStr, oldPriceColor);

  const newPriceStr = formatPrice(newPrice, '0,00 €');

  return renderBundledPrice(newPriceStr, newPriceColor, oldPriceStr, oldPriceColor);
}

export function chooseValueByLang(values: { [k in Language]?: string }) {
  return stateful(() => {
    const language = useObservable(() => globalLanguageSubject, []);
    if (!language) return html`<i>no language loaded</i>`;
    return unsafeHTML(values[language] ?? '<i>there is no description in this language</i>');
  })();
}

export function renderBundledPrice(newPriceStr: string, newColor: string, oldPriceStr: string, oldColor: string) {
  return html`
    <tr-flex horizontal space="sm" end crossaxis-center>
      <tr-h4 bold ${css({ textDecoration: 'line-through', color: oldColor })}> ${oldPriceStr} </tr-h4>
      <tr-h3 bold ${css({ color: newColor })} bold align-end> ${newPriceStr} </tr-h3>
    </tr-flex>
  `;
}

export const timeSlotTime = stateful<ProductCardData>(({ start, end, colorDef }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const windowSize = useObservable(() => windowSizeObservable, []);

  if (!theme) throw new Error('theme is undefined');
  if (!windowSize) throw new Error('window size is undefined');

  const translate = clientTranslator.translate.bind(clientTranslator);

  if (!start || !end) return html` <tr-text> --- </tr-text> `;
  return html`
    <tr-flex
      space="xxs"
      ${css({
        background: colorDef.productColor,
        color: '#fff',
        padding: theme.spacing.sm,
        borderRadius: theme.sizes.borderRadius,
      })}
    >
      <tr-flex space="xxs" crossaxis-center>
        ${(() => {
          const displayDay = (text: string, day: number, month: string | null) => html`
            <tr-h3 bold>
              <tr-text small ${css({ display: 'inline-block' })}> ${text} </tr-text>
              ${day} ${month}
            </tr-h3>
          `;
          // same day
          if (start.day === end.day) return displayDay(translate('on'), start.day, start.monthShort);

          // two days
          if (start.day + 1 === end.day)
            return html`
              ${displayDay(translate('on'), start.day, start.monthShort)}
              ${displayDay(translate('and'), end.day, end.monthShort)}
            `;

          // more then two days
          return html`
            ${displayDay(translate('from'), start.day, start.monthShort)}
            ${displayDay(translate('till'), end.day, end.monthShort)}
          `;
        })()}
      </tr-flex>
    </tr-flex>
  `;
});

export const productCardPrice = stateful<ProductCardData>(({ colorDef, applyedBundle, price }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  if (!theme) throw new Error('theme is undefined');

  return !!applyedBundle?.price
    ? renderPrice(0, applyedBundle.price, colorDef.newPriceColor, colorDef.oldPriceColor)
    : !!price
    ? renderPrice(price, null, colorDef.newPriceColor, colorDef.oldPriceColor)
    : (nothing as any as TemplateResult);
});

export const getProductTags = (
  product: NarrowedEntity<
    Product,
    {
      _id: 1;
      isLDAPOnly: 1;
      timeframe: { name: 1 };
      presenceType: 1;
      language: 1;
      topic: 1;
      settings: { courseLocation: 1; inheritedCourseLocation: 1 };
      productEntryRequirementLabels: 1;
    }
  >,
) =>
  globalLanguageSubject.pipe(
    map(() => {
      const translate = clientTranslator.translate.bind(clientTranslator);

      const labelsIterator = (function* () {
        if (product.isLDAPOnly)
          yield { icon: 'person', label: translate('PROFILE', 'LDAP_ACCOUNT_REQUIRED') as string };
        // if (product.timeframe) yield { icon: 'event', label: product.timeframe.name };
        if (product.presenceType && product.presenceType !== 'not_selected') {
          yield { icon: 'support_agent', label: translate('PRODUCT', 'presenceTypes', product.presenceType) as string };
        }

        if (product.language)
          yield { icon: 'language', label: clientTranslator.translate('languages', product.language) };
        if (product.topic) yield { icon: 'tag', label: product.topic };

        const courseLocation = product.settings.courseLocation || product.settings.inheritedCourseLocation?.[1];
        if (courseLocation) yield { icon: 'location_on', label: courseLocation };
      })();

      const labels = Array.from(labelsIterator);

      return [
        ...labels,
        ...(product.productEntryRequirementLabels?.map(label => ({ icon: 'bolt', label: trn(label) })) ?? []),
      ];
    }),
  );

export function renderProductCardTags(tags: { icon: string; label: string }[]) {
  return html`
    <tr-flex flex space="xxs" horizontal wrap ${css({ marginTop: '8px', gap: '16px', padding: '5px' })}>
      ${tags.map(
        tag => html`
          <tr-flex horizontal center crossaxis-center space="xxs" ${css({ padding: '0' })}>
            <tr-icon> ${tag.icon} </tr-icon>
            <tr-text> ${tag.label} </tr-text>
          </tr-flex>
        `,
      )}
    </tr-flex>
  `;
}

export const renderDateTimeBoxPair = stateful<{
  date: DateTime;
  startTime: string;
  endTime: string;
  colorDef: ProductCardData['colorDef'];
  timeStackStyle: 'vertical' | 'horizontal';
}>(({ date, startTime, endTime, colorDef, timeStackStyle }) => {
  const translate = clientTranslator.translate.bind(clientTranslator);
  const language = useObservable(() => globalLanguageSubject, [42]);

  if (!language) return nothing;

  const dateFormatter = new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
  });

  const dateString = dateFormatter.format(date.toJSDate());

  return html`
    <tr-flex
      ${css({
        padding: '8px 12px',
        borderRadius: '4px',
        background: colorDef.layoutLightContrastColor,
        whiteSpace: 'nowrap',
      })}
      crossaxis-center
      center
    >
      <tr-text ${css({ fontSize: timeStackStyle === 'horizontal' ? '17px' : '20px' })}>${dateString}</tr-text>
    </tr-flex>
    <tr-flex
      vertical
      ${css({
        padding: '8px 12px',
        borderRadius: '4px',
        background: colorDef.layoutLightContrastColor,
        whiteSpace: 'nowrap',
      })}
      crossaxis-center
      center
    >
      ${timeStackStyle === 'horizontal'
        ? html` <tr-text ${css({ fontSize: '17px' })}> ${startTime} - ${endTime}</tr-text> `
        : html`
            <tr-text ${css({ fontSize: '14px', lineHeight: '1.' })}
              >${startTime} - <br />
              ${endTime} ${translate('uhr')}
            </tr-text>
          `}
    </tr-flex>
  `;
});
